import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
    ACQUISITION_BACKEND,
    authTokenKey,
    MONO_REPO_ACCQUISTION_BACKEND,
} from '@/constants';
import i18n from '@/translations/client-setup';
import {
    Area,
    ICity,
    ILeadRequest,
    ILeadResponse,
    IMakesApi_Result_SELL,
    IModelApi_Result_SELL,
    Region,
} from '@/types';
import { IShouldBuyResponse } from '@/types/acquistion/atomic-sell-form.types';
import { authStorage } from '@/classes/SylndrStorage';
import { baseQueryWrapper } from '@/libs/baseQueryWrapper.lib';

// Define our single API slice object

export const acquistionApiSlice = createApi({
    // The cache reducer expects to be added at `state.acquistion` (default is state.api - this is optional)
    reducerPath: 'acquistion',
    baseQuery: fetchBaseQuery({
        baseUrl: ACQUISITION_BACKEND,
        prepareHeaders: headers => {
            headers.set('lang', `${i18n.language}`);
            headers.set('origin', 'sylndr.com');
            headers.set('referer', 'sylndr.com');
            headers.set('Authority', 'sylndr.com');
            const userToken = authStorage.getItem(authTokenKey);
            if (userToken) headers.set('Authorization', `Bearer ${userToken}`);
            return headers;
        },
    }),
    tagTypes: [],
    // The "endpoints" represent operations and requests for this server
    endpoints: builder => ({
        // The getMakesV2 fetches all makes in or out of the shopping list
        getMakesV3: builder.query<IMakesApi_Result_SELL, void>({
            query: () => 'v1.0/make',
        }),
        getModelsV3: builder.query<IModelApi_Result_SELL, string>({
            query: makeId => `v1.0/model/${makeId}`,
        }),

        getRegions: builder.query<Region[], void>({
            query: () => 'v1.0/areas',
            transformResponse: (response: { data: ICity[] }) => {
                const regions = response.data;
                const formatttedRegions: Region[] = [];
                for (const city of regions) {
                    const formattedRegion: Region = {
                        id: city.cityId,
                        bigArea: city.cityEnName,
                        arBigArea: city.cityArName,
                        smallArea: city.areas.map(area => {
                            return {
                                id: area.areaId,
                                polygon: area.coordinates.map(coord => {
                                    return {
                                        lng: coord.longitude,
                                        lat: coord.latitude,
                                    };
                                }),
                                name: area.areaEnName,
                                arName: area.areaArName,
                                serviceable: area.serviceable,
                                warehouse: area.warehouse,
                            } as Area;
                        }),
                    };
                    formatttedRegions.push(formattedRegion);
                }
                return formatttedRegions;
            },
        }),
        // add lead
        addLead: builder.mutation<ILeadResponse, ILeadRequest>({
            query: data => ({
                url: '/v1.0/selling-requests',
                method: 'POST',
                body: data,
            }),
        }),
    }),
});

// Separate API slice for shopping list with different base URL
export const shoppingListApiSlice = createApi({
    reducerPath: 'shoppingList',
    baseQuery: fetchBaseQuery({
        baseUrl: MONO_REPO_ACCQUISTION_BACKEND,
        prepareHeaders: headers => {
            headers.set('lang', `${i18n.language}`);
            headers.set('origin', 'sylndr.com');
            headers.set('referer', 'sylndr.com');
            headers.set('Authority', 'sylndr.com');
            const userToken = authStorage.getItem(authTokenKey);
            if (userToken) headers.set('Authorization', `Bearer ${userToken}`);
            return headers;
        },
    }),
    tagTypes: [],
    endpoints: builder => ({
        // check if model should be bought
        checkShouldBuy: builder.query<IShouldBuyResponse, string>({
            query: modelId => `/shopping-list/should-buy/${modelId}`,
        }),
    }),
});

export const acquisitionInterceptorApiSlice = createApi({
    reducerPath: 'sellingRequestsInterceptor',
    baseQuery: baseQueryWrapper({ baseUrl: `${ACQUISITION_BACKEND}` }),
    tagTypes: ['Checkout'],
    endpoints: builder => ({
        getInspectionVisit: builder.query<any, void>({
            query: () => ({
                url: '/v1.0/selling-requests/my-selling-requests',
                method: 'GET',
            }),
        }),
    }),
});

const { useGetInspectionVisitQuery } = acquisitionInterceptorApiSlice;

// Export the auto-generated hooks
const {
    useLazyGetMakesV3Query,
    useLazyGetModelsV3Query,
    useLazyGetRegionsQuery,
    useAddLeadMutation,
} = acquistionApiSlice;

// Export the shopping list hooks
const { useLazyCheckShouldBuyQuery } = shoppingListApiSlice;

export {
    useGetInspectionVisitQuery,
    useLazyGetMakesV3Query,
    useLazyGetModelsV3Query,
    useLazyGetRegionsQuery,
    useLazyCheckShouldBuyQuery,
    useAddLeadMutation,
};
